import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import type {OnDemandResourcesSliceFragment} from '@/graphql-types'
import type {OnDemandResourcePagesQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {EventCardsWithFilters} from '@/sections/event-cards-with-filters'
import Image from '@helios/shared/components/image'
import LocalizedLink from '@helios/shared/components/localized-link'
import type PrismicLink from '@helios/shared/components/prismic-link'

export const OnDemandResourcesSlice = ({primary}: OnDemandResourcesSliceFragment) => {
    const {allPrismicOnDemandResourcePage} = useStaticQuery<OnDemandResourcePagesQuery>(query)
    const resourcePages = useLocalizedContent(allPrismicOnDemandResourcePage.nodes).map(
        ({data, uid}) => ({
            ...data,
            to: `/webinars/${uid}`,
        })
    )

    const data = resourcePages.map((resourcePage) => {
        return {
            title: resourcePage.title,
            description: resourcePage.description,
            category: resourcePage.category.document.data.name,
            language: resourcePage.language,
            duration: resourcePage.duration,
            Image: () => <Image image={resourcePage.title_image} />,
            CTA: (props: React.ComponentProps<typeof PrismicLink>) => (
                <LocalizedLink {...props} to={resourcePage.to}>
                    {primary.resource_cta_text}
                </LocalizedLink>
            ),
        }
    })

    return (
        <EventCardsWithFilters
            title={primary.title}
            topLink={primary.top_link}
            topLinkText={primary.top_link_text}
            eventsData={data}
            layout={primary.layout}
            eventCtaText={primary.resource_cta_text}
            filters={[
                {
                    name: 'category',
                    placeholder: primary.filter_resource_type_placeholder,
                    allLabel: primary.filter_resource_type_all,
                },
                {
                    name: 'language',
                    placeholder: primary.filter_language_placeholder,
                    allLabel: primary.filter_language_all,
                },
            ]}
            showingAllResultsLabel={primary.showing_all_results}
            showingResultsForLabel={primary.showing_all_results_for}
            clearText={primary.filter_clear}
            noEventsFoundText={primary.no_resources_found}
            searchPlaceholder={primary.search_placeholder}
        />
    )
}

export const onDemandResourcesSliceFragment = graphql`
    fragment OnDemandResourcesSlice on PrismicSlicePageDataBodyOnDemandResources {
        __typename
        primary {
            title
            top_link_text
            top_link {
                ...PrismicLink
            }
            layout
            resource_cta_text
            showing_all_results
            showing_all_results_for
            filter_resource_type_placeholder
            filter_resource_type_all
            filter_language_placeholder
            filter_language_all
            filter_clear
            no_resources_found
            search_placeholder
        }
    }
`

export const query = graphql`
    query OnDemandResourcePages($imageBreakpoints: [Int], $threeColumns: String) {
        allPrismicOnDemandResourcePage {
            nodes {
                _previewable
                uid
                lang
                type
                last_publication_date
                data {
                    title
                    title_image {
                        alt
                        copyright
                        dimensions {
                            height
                            width
                        }
                        url
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                    duration
                    description
                    category {
                        document {
                            ... on PrismicOnDemandResourceCategory {
                                id
                                data {
                                    name
                                }
                            }
                        }
                    }
                    language
                }
            }
        }
    }
`
